import { useEffect, useState } from 'react';
import type { EmployeeDto, GreetingPart } from '../../api/scan.js';
import { translate } from '../../i18n/index.js';
import { WorkerAvatar } from '../ui/WorkerAvatar.js';
import { greetingPartNow } from './greeting.js';

type Greet = { employee: EmployeeDto; greeting: GreetingPart };

/**
 * U-1009-09 — full-screen „בוקר טוב <שם>" on the first badge scan of the
 * Israel day. Fed by scanApi.badge/identify (event 'edal:greet'), so every
 * kiosk gets it. Speaks the employee's own language. Gone after 3s or any
 * tap/key.
 */
export function GreetingSplash(): JSX.Element | null {
  const [g, setG] = useState<Greet | null>(null);

  useEffect(() => {
    const on = (e: Event) => {
      const d = (e as CustomEvent<{ employee: EmployeeDto; greeting?: GreetingPart }>).detail;
      setG({ employee: d.employee, greeting: d.greeting ?? greetingPartNow() });
    };
    window.addEventListener('edal:greet', on);
    return () => window.removeEventListener('edal:greet', on);
  }, []);

  useEffect(() => {
    if (!g) return;
    const timer = window.setTimeout(() => setG(null), 3000);
    const close = () => setG(null);
    window.addEventListener('keydown', close);
    return () => { window.clearTimeout(timer); window.removeEventListener('keydown', close); };
  }, [g]);

  if (!g) return null;
  const lang = g.employee.preferredLanguage === 'ru' ? 'ru' : 'he';
  const first = g.employee.name.trim().split(/\s+/)[0] ?? g.employee.name;
  const text = translate(lang, `badge.greet.${g.greeting}`, { name: first });
  return (
    <div
      role="status" aria-live="polite"
      dir={lang === 'ru' ? 'ltr' : 'rtl'}
      onClick={() => setG(null)}
      className="fixed inset-0 z-[100] flex flex-col items-center justify-center gap-8 bg-navy-600 text-white motion-safe:animate-in motion-safe:fade-in motion-safe:duration-200"
    >
      <WorkerAvatar name={g.employee.name} photoUrl={g.employee.photoUrl} size="xl"
        className="!w-[220px] !h-[220px] !text-7xl border-4 border-white/80" />
      <p className="text-5xl font-bold text-center px-6">{text}</p>
    </div>
  );
}
